import {
  computed,
  isRef,
  reactive,
  ref,
  readonly,
  shallowRef,
  toRefs,
  unref,
  watch,
} from "vue";
import { tryOnUnmounted, useTimeoutFn } from "@vueuse/core";
import { dispatchWarn } from "@/helpers/functions/FunctionHelpers";
import { isSafeArray } from "@/helpers/functions/ArrayHelpers";

const STATUS = Object.freeze({
  IDLE: "idle",
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
});

/**
 * @typedef {string | number | string[] | number[]} QueryKeyValue
 * @typedef {string | number | import("vue").Ref<QueryKeyValue>} QueryKeyEntry
 * @typedef {QueryKeyEntry[] | import("vue").Ref<QueryKeyEntry[]>} QueryKey
 */

/**
 * @typedef {boolean} QueryShallow
 * @typedef {boolean | import("vue").Ref<QueryShallow>} QueryShallowDef
 */

/**
 * @template TData
 * @template TError
 * @template TDataInitial
 * @typedef {Object} UseQueryState
 * @property {TData | TDataInitial} data
 * @property {TError | null} error
 * @property {"idle" | "pending" | "success" | "error"} status
 * @property {boolean} isFetching
 * @property {boolean} isStale
 */

/**
 * @template TData
 * @template TError
 * @template TDataInitial
 * @typedef {import("vue").ToRefs<Readonly<UseQueryState<TData, TError, TDataInitial>>> & {
 *   refetch: () => Promise<TData | undefined>,
 *   isError: import('vue').Ref<boolean>,
 *   isPending: import('vue').Ref<boolean>,
 *   isSuccess: import('vue').Ref<boolean>,
 * }} UseQueryReturn
 */

/**
 * @template TData
 * @template TResponse
 * @template TDataInitial
 * @typedef {Object} UseQueryOptions
 * @property {QueryKey} key
 * @property {QueryShallowDef} shallow
 * @property {() => TResponse | Promise<TResponse>} queryFn
 * @property {(response: TResponse) => TData} [select]
 * @property {number} [staleTime=0]
 * @property {TDataInitial | (() => TDataInitial)} [initialData]
 */

/**
 * Executa uma consulta e repete a chamada quando qualquer parte reativa da
 * chave mudar.
 *
 * @example
 * const documentType = ref("contracts");
 * const { data, status, refetch } = useQuery({
 *   key: ["documents", documentType],
 *   queryFn: () => getDocuments(documentType.value),
 *   staleTime: 10 * 1000,
 *   select: (response) => response.data,
 * });
 *
 * @template TData
 * @template [TError=unknown]
 * @template [TDataInitial=undefined]
 * @template [TResponse=TData]
 * @param {UseQueryOptions<TData, TResponse, TDataInitial>} options
 * @returns {UseQueryReturn<TData, TError, TDataInitial>}
 */
export function useQuery(options) {
  const queryOptions = options ?? {};
  const hasValidKey = isRef(queryOptions.key)
    ? isSafeArray(queryOptions.key.value)
    : isSafeArray(queryOptions.key);

  const hasValidQueryFn = typeof queryOptions.queryFn === "function";
  const hasRequiredOptions = hasValidKey && hasValidQueryFn;

  if (!hasValidKey) {
    dispatchWarn(
      "useQuery: a propriedade obrigatória 'key' deve ser uma array."
    );
  }

  if (!hasValidQueryFn) {
    dispatchWarn(
      "useQuery: a propriedade obrigatória 'queryFn' deve ser uma função."
    );
  }

  const staleTime = Math.max(0, queryOptions.staleTime ?? 0);
  const isInitialDataFn = typeof queryOptions.initialData === "function";
  const initialData = isInitialDataFn
    ? queryOptions.initialData()
    : queryOptions.initialData;

  const dataRef = unref(queryOptions.shallow)
    ? shallowRef(initialData)
    : ref(initialData);

  const queryKey = computed(() =>
    hasValidKey
      ? isRef(queryOptions.key)
        ? queryOptions.key.value
        : queryOptions.key
      : []
  );

  /** @type {Omit<UseQueryState<TData, TError, TDataInitial>, "data">} */
  const queryState = reactive({
    error: null,
    status: STATUS.IDLE,
    isFetching: false,
    isStale: true,
  });

  const activeRequest = shallowRef(0);
  const disposed = shallowRef(false);

  const isError = computed(() => queryState.status === STATUS.ERROR);
  const isSuccess = computed(() => queryState.status === STATUS.SUCCESS);
  const isPending = computed(() => queryState.status === STATUS.PENDING);

  const resolvedKey = computed(() =>
    queryKey.value.flatMap((entry) => {
      const value = unref(entry);
      return isSafeArray(value) ? value : [value];
    })
  );

  const staleControls = useTimeoutFn(
    () => {
      queryState.isStale = true;
    },
    staleTime,
    { immediate: false }
  );

  function scheduleStaleState() {
    staleControls.stop();

    if (staleTime === Infinity) return;
    if (staleTime === 0) {
      queryState.isStale = true;
      return;
    }

    staleControls.start();
  }

  async function executeQuery() {
    if (!hasRequiredOptions) return undefined;

    const request = ++activeRequest.value;

    queryState.error = null;
    queryState.isFetching = true;
    queryState.status = STATUS.PENDING;

    try {
      const response = await queryOptions.queryFn();
      const data = queryOptions.select
        ? queryOptions.select(response)
        : response;

      if (disposed.value || request !== activeRequest.value) return undefined;

      dataRef.value = data;
      queryState.status = STATUS.SUCCESS;
      queryState.isStale = false;
      scheduleStaleState();

      return data;
    } catch (error) {
      if (disposed.value || request !== activeRequest.value) return undefined;

      queryState.error = error;
      queryState.status = STATUS.ERROR;
      queryState.isStale = true;

      return undefined;
    } finally {
      if (!disposed.value && request === activeRequest.value) {
        queryState.isFetching = false;
      }
    }
  }

  const stopQueryWatcher = watch(resolvedKey, executeQuery, {
    deep: true,
    immediate: true,
  });

  tryOnUnmounted(() => {
    disposed.value = true;
    activeRequest.value += 1;
    stopQueryWatcher();
    staleControls.stop();
  });

  return {
    data: readonly(dataRef),
    ...toRefs(readonly(queryState)),
    isError,
    isSuccess,
    isPending,
    refetch: executeQuery,
  };
}
